/**
 * Despia / RevenueCat in-app purchase bridge.
 *
 * The web app is wrapped by Despia for the App Store and Google Play. Digital
 * goods there must go through Apple IAP / Google Play Billing, which Despia
 * exposes via its RevenueCat module. This module normalises the three shapes
 * a Despia build can present, in priority order:
 *
 *  1. `window.Purchases` — the @revenuecat/purchases-capacitor plugin.
 *  2. `window.despia.iap` — Despia's JS object API.
 *  3. `despia://` deeplinks + globals written back by the native shell
 *     (`window.subscriptionStatus`, `window.purchaseResult`).
 *
 * Nothing here runs on web: callers gate on PaymentRouter.isMobileNative.
 */

export interface IapResult {
  ok: boolean;
  cancelled?: boolean;
  entitlementActive?: boolean;
  error?: string;
  raw?: unknown;
}

const w = () => window as any;

/** Fire a despia:// deeplink (Despia intercepts the navigation). */
function callDeeplink(command: string) {
  const bridge = w().despia;
  if (typeof bridge === "function") {
    bridge(`despia://${command}`);
    return;
  }
  window.location.href = `despia://${command}`;
}

/** Poll a global the native shell writes back after a deeplink command. */
function waitForGlobal(keys: string[], timeoutMs = 120000): Promise<any> {
  const start = Date.now();
  keys.forEach((k) => {
    try { delete w()[k]; } catch { /* non-configurable */ }
  });
  return new Promise((resolve) => {
    const tick = () => {
      for (const k of keys) {
        const v = w()[k];
        if (v !== undefined && v !== null && v !== "") return resolve(v);
      }
      if (Date.now() - start > timeoutMs) return resolve(undefined);
      setTimeout(tick, 400);
    };
    tick();
  });
}

function normalise(raw: any): IapResult {
  if (raw === undefined) return { ok: false, error: "No response from the store." };
  const val = typeof raw === "string" ? raw.toLowerCase() : raw;
  if (typeof val === "string") {
    if (val.includes("cancel")) return { ok: false, cancelled: true, raw };
    if (val.includes("error") || val.includes("fail")) return { ok: false, error: raw, raw };
    return { ok: true, entitlementActive: val.includes("active") || val === "true" || val.includes("success"), raw };
  }
  const entitlements = val?.customerInfo?.entitlements?.active ?? val?.entitlements?.active;
  const active = entitlements ? Object.keys(entitlements).length > 0 : Boolean(val?.active ?? val?.isActive);
  if (val?.cancelled || val?.userCancelled) return { ok: false, cancelled: true, raw };
  return { ok: true, entitlementActive: active, raw };
}

export const DespiaIap = {
  /** True when some RevenueCat bridge is reachable in this build. */
  isAvailable(): boolean {
    const g = w();
    return Boolean(g.Purchases?.purchasePackage || g.despia?.iap?.purchase || typeof g.despia !== "undefined");
  },

  /** Buy a product / RevenueCat package identifier. */
  async purchase(productId: string): Promise<IapResult> {
    const g = w();
    try {
      if (g.Purchases?.purchasePackage) {
        const offerings = await g.Purchases.getOfferings();
        const packages = offerings?.current?.availablePackages ?? [];
        const pkg =
          packages.find((p: any) => p.identifier === productId) ??
          packages.find((p: any) => p.product?.identifier === productId) ??
          packages[0];
        if (!pkg) return { ok: false, error: "No store package is available for this plan." };
        const res = await g.Purchases.purchasePackage({ aPackage: pkg });
        return normalise(res);
      }

      if (g.despia?.iap?.purchase) {
        const res = await g.despia.iap.purchase(productId);
        return normalise(res ?? "success");
      }

      callDeeplink(`revenuecat-purchase?product=${encodeURIComponent(productId)}`);
      return normalise(await waitForGlobal(["purchaseResult", "subscriptionStatus"]));
    } catch (e: any) {
      if (e?.userCancelled || /cancel/i.test(e?.message ?? "")) return { ok: false, cancelled: true };
      return { ok: false, error: e?.message ?? "Purchase failed." };
    }
  },

  /** Restore previous purchases (required by Apple review). */
  async restore(): Promise<IapResult> {
    const g = w();
    try {
      if (g.Purchases?.restorePurchases) return normalise(await g.Purchases.restorePurchases());
      if (g.despia?.iap?.restore) return normalise((await g.despia.iap.restore()) ?? "success");
      callDeeplink("revenuecat-restore");
      return normalise(await waitForGlobal(["restoreResult", "subscriptionStatus"], 60000));
    } catch (e: any) {
      return { ok: false, error: e?.message ?? "Restore failed." };
    }
  },

  /** Current entitlement state, when the bridge can report it. */
  async entitlementActive(): Promise<boolean> {
    const g = w();
    try {
      if (g.Purchases?.getCustomerInfo) return normalise(await g.Purchases.getCustomerInfo()).entitlementActive === true;
      if (g.despia?.iap?.getCustomerInfo) return normalise(await g.despia.iap.getCustomerInfo()).entitlementActive === true;
      if (typeof g.subscriptionStatus !== "undefined") return normalise(g.subscriptionStatus).entitlementActive === true;
    } catch { /* ignore */ }
    return false;
  },

  /** Link the RevenueCat customer to the app user so entitlements follow them. */
  async identify(appUserId: string): Promise<void> {
    const g = w();
    try {
      if (g.Purchases?.logIn) { await g.Purchases.logIn({ appUserID: appUserId }); return; }
      if (g.despia?.iap?.login) { await g.despia.iap.login(appUserId); return; }
      callDeeplink(`revenuecat-login?userid=${encodeURIComponent(appUserId)}`);
    } catch { /* non-fatal */ }
  },
};